import React from 'react';
import { useNavigate } from 'react-router-dom';
import { logOut } from '../../api/auth';
import styles from './UserMenu.module.css';

const ICO_LOGOUT = 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9';
const ICO_USER   = 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z';

const Icon = ({ d, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d={d} />
  </svg>
);

function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  let color = '#';
  for (let i = 0; i < 3; i++) color += `00${((hash >> (i * 8)) & 0xff).toString(16)}`.slice(-2);
  return color;
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function UserMenu({ user }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleLogout = () => { setOpen(false); logOut(); navigate('/login'); };

  const name = user?.full_name || user?.email || 'User';
  const avatarColor = stringToColor(name);

  return (
    <div className={styles.wrap} ref={ref}>
      <button
        className={styles.trigger}
        onClick={() => setOpen(v => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Account menu"
      >
        <span className={styles.avatar} style={{ background: avatarColor }} aria-hidden="true">
          {initials(name)}
        </span>
      </button>

      {open && (
        <div className={styles.popover} role="menu" aria-label="Account options">
          <div className={styles.identity}>
            <span className={styles.avatarLg} style={{ background: avatarColor }}>
              {initials(name)}
            </span>
            <div className={styles.identityBody}>
              {user?.full_name && <div className={styles.identityName}>{user.full_name}</div>}
              {user?.email && <div className={styles.identityEmail}>{user.email}</div>}
            </div>
          </div>

          <div className={styles.divider} />

          <button className={styles.item} role="menuitem" onClick={() => { navigate('/me'); setOpen(false); }}>
            <Icon d={ICO_USER} size={14} />
            My account
          </button>

          <div className={styles.divider} />

          <button className={`${styles.item} ${styles.itemDanger}`} role="menuitem" onClick={handleLogout}>
            <Icon d={ICO_LOGOUT} size={14} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
